import React, { useEffect, useState, useCallback } from 'react';
import { mcxSettlementApi, clientsApi } from '../../lib/api';
import { GroupBox } from '../shared/GroupBox';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';
import { 
  RefreshCw, FileSpreadsheet, FileText, Printer, Users, Building2,
  TrendingUp, TrendingDown, Search
} from 'lucide-react';
import { toast } from 'sonner';

export function MCXSettlementPage() {
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('all'); // all, client, parent
  const [selectedId, setSelectedId] = useState('');
  const [settlementDate, setSettlementDate] = useState('');
  
  // Data
  const [clientsList, setClientsList] = useState({ clients: [], parents: [] });
  const [toPay, setToPay] = useState([]);
  const [toReceive, setToReceive] = useState([]);
  const [totals, setTotals] = useState({ to_pay_total: 0, to_receive_total: 0, net_balance: 0 });
  const [selectedEntity, setSelectedEntity] = useState(null);

  // Fetch clients/parents for left panel
  const fetchClientsList = useCallback(async () => {
    try {
      const data = await mcxSettlementApi.getClientsList();
      setClientsList(data);
    } catch (error) {
      console.error('Failed to fetch clients list:', error);
    }
  }, []);

  // Fetch settlement data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let result;
      
      if (viewMode === 'client' && selectedId) {
        result = await mcxSettlementApi.viewByClient(selectedId, { settlement_date: settlementDate || undefined });
        setSelectedEntity(result.client);
      } else if (viewMode === 'parent' && selectedId) {
        result = await mcxSettlementApi.viewByParent(selectedId, { settlement_date: settlementDate || undefined });
        setSelectedEntity(result.parent);
      } else {
        result = await mcxSettlementApi.list({ settlement_date: settlementDate || undefined });
        setSelectedEntity(null);
      }
      
      setToPay(result.to_pay || []);
      setToReceive(result.to_receive || []);
      setTotals({
        to_pay_total: result.to_pay_total || 0,
        to_receive_total: result.to_receive_total || 0,
        net_balance: result.net_balance || 0
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load settlement data');
    } finally {
      setLoading(false);
    }
  }, [viewMode, selectedId, settlementDate]);

  useEffect(() => {
    fetchClientsList();
  }, [fetchClientsList]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle entity selection
  const handleSelectClient = (clientId) => {
    setViewMode('client');
    setSelectedId(clientId);
  };

  const handleSelectParent = (parentId) => {
    setViewMode('parent');
    setSelectedId(parentId);
  };

  const handleShowAll = () => {
    setViewMode('all');
    setSelectedId('');
    setSelectedEntity(null);
  };

  // Export handlers
  const handleExportXlsx = () => {
    const params = { settlement_date: settlementDate };
    if (viewMode === 'client' || viewMode === 'parent') {
      params.client_id = selectedId;
    }
    window.open(mcxSettlementApi.exportXlsx(params), '_blank');
  };

  const handleExportPdf = () => {
    const params = { settlement_date: settlementDate };
    if (viewMode === 'client' || viewMode === 'parent') {
      params.client_id = selectedId;
    }
    window.open(mcxSettlementApi.exportPdf(params), '_blank');
  };

  const formatAmount = (val) => {
    const num = parseFloat(val) || 0;
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="h-full flex gap-3" data-testid="mcx-settlement-page">
      {/* Left Panel - Client/Parent Selector */}
      <div className="w-64 flex-shrink-0 flex flex-col border rounded bg-card">
        <div className="p-2 border-b bg-muted">
          <div className="flex items-center gap-2 mb-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Select View</span>
          </div>
          <Button 
            variant={viewMode === 'all' ? 'default' : 'outline'} 
            size="sm" 
            className="w-full mb-1"
            onClick={handleShowAll}
          >
            Show All
          </Button>
        </div>
        
        <div className="flex-1 overflow-auto">
          {/* Clients */}
          <div className="p-2 border-b">
            <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground mb-2">
              <Users className="h-3 w-3" />
              CLIENTS ({clientsList.clients.length})
            </div>
            <div className="space-y-1 max-h-[200px] overflow-auto">
              {clientsList.clients.map(c => (
                <button
                  key={c.client_id}
                  className={cn(
                    "w-full text-left px-2 py-1.5 rounded text-xs hover:bg-muted transition-colors",
                    viewMode === 'client' && selectedId === c.client_id && "bg-primary/20 text-primary"
                  )}
                  onClick={() => handleSelectClient(c.client_id)}
                >
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-muted-foreground">
                    {c.count} entries • ₹{formatAmount(c.amount)}
                  </div>
                </button>
              ))}
              {clientsList.clients.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-2">No settled clients</p>
              )}
            </div>
          </div>
          
          {/* Parents */}
          <div className="p-2">
            <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground mb-2">
              <Building2 className="h-3 w-3" />
              PARENTS ({clientsList.parents.length})
            </div>
            <div className="space-y-1 max-h-[200px] overflow-auto">
              {clientsList.parents.map(p => (
                <button
                  key={p.parent_id}
                  className={cn(
                    "w-full text-left px-2 py-1.5 rounded text-xs hover:bg-muted transition-colors",
                    viewMode === 'parent' && selectedId === p.parent_id && "bg-primary/20 text-primary"
                  )}
                  onClick={() => handleSelectParent(p.parent_id)}
                >
                  <div className="font-medium truncate">{p.name}</div>
                  <div className="text-muted-foreground">
                    {p.child_count} children • ₹{formatAmount(p.amount)}
                  </div>
                </button>
              ))}
              {clientsList.parents.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-2">No parents</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - To Pay / To Receive Grids */}
      <div className="flex-1 flex flex-col gap-3">
        {/* Toolbar */}
        <div className="flex items-center justify-between p-2 bg-card border rounded">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-primary">
              MCX Settlement
              {selectedEntity && ` - ${selectedEntity.name}`}
              {viewMode === 'parent' && ' (Grouped)'}
            </span>
            <Input
              type="date"
              value={settlementDate}
              onChange={(e) => setSettlementDate(e.target.value)}
              className="h-8 w-36"
              placeholder="Filter by date"
            />
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={handleExportXlsx}>
              <FileSpreadsheet className="h-4 w-4 mr-1" />Excel
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPdf}>
              <FileText className="h-4 w-4 mr-1" />PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.print()}>
              <Printer className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Grids Container */}
        <div className="flex-1 grid grid-cols-2 gap-3 min-h-0">
          {/* To Receive Grid */}
          <GroupBox label={<span className="flex items-center gap-1 text-emerald-600"><TrendingUp className="h-4 w-4" />To Receive</span>}>
            <div className="h-full flex flex-col">
              <div className="flex-1 overflow-auto">
                <table className="w-full text-xs border-collapse">
                  <thead className="bg-emerald-50 dark:bg-emerald-950/30 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left border font-semibold">Date</th>
                      <th className="px-2 py-1.5 text-left border font-semibold">ID</th>
                      <th className="px-2 py-1.5 text-left border font-semibold">Exchange</th>
                      <th className="px-2 py-1.5 text-right border font-semibold">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {toReceive.map((entry, idx) => (
                      <tr key={entry.entry_id || idx} className="border-b hover:bg-muted/30">
                        <td className="px-2 py-1 border">{entry.settlement_date}</td>
                        <td className="px-2 py-1 border font-medium">{entry.id_display || entry.code_or_id}</td>
                        <td className="px-2 py-1 border">{entry.exchange}</td>
                        <td className="px-2 py-1 border text-right font-mono text-emerald-600">
                          {formatAmount(entry.amount)}
                        </td>
                      </tr>
                    ))}
                    {toReceive.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-2 py-4 text-center text-muted-foreground">
                          No entries to receive
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Footer */}
              <div className="border-t bg-emerald-50/50 dark:bg-emerald-950/20 p-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">{toReceive.length} entries</span>
                  <span className="text-sm font-bold font-mono text-emerald-600">
                    ₹{formatAmount(totals.to_receive_total)}
                  </span>
                </div>
              </div>
            </div>
          </GroupBox>

          {/* To Pay Grid */}
          <GroupBox label={<span className="flex items-center gap-1 text-red-600"><TrendingDown className="h-4 w-4" />To Pay</span>}>
            <div className="h-full flex flex-col">
              <div className="flex-1 overflow-auto">
                <table className="w-full text-xs border-collapse">
                  <thead className="bg-red-50 dark:bg-red-950/30 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left border font-semibold">Date</th>
                      <th className="px-2 py-1.5 text-left border font-semibold">ID</th>
                      <th className="px-2 py-1.5 text-left border font-semibold">Exchange</th>
                      <th className="px-2 py-1.5 text-right border font-semibold">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {toPay.map((entry, idx) => (
                      <tr key={entry.entry_id || idx} className="border-b hover:bg-muted/30">
                        <td className="px-2 py-1 border">{entry.settlement_date}</td>
                        <td className="px-2 py-1 border font-medium">{entry.id_display || entry.code_or_id}</td>
                        <td className="px-2 py-1 border">{entry.exchange}</td>
                        <td className="px-2 py-1 border text-right font-mono text-red-600">
                          {formatAmount(entry.amount)}
                        </td>
                      </tr>
                    ))}
                    {toPay.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-2 py-4 text-center text-muted-foreground">
                          No entries to pay
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Footer */}
              <div className="border-t bg-red-50/50 dark:bg-red-950/20 p-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">{toPay.length} entries</span>
                  <span className="text-sm font-bold font-mono text-red-600">
                    ₹{formatAmount(totals.to_pay_total)}
                  </span>
                </div>
              </div>
            </div>
          </GroupBox>
        </div>

        {/* Net Balance Footer */}
        <div className="p-3 bg-card border rounded">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Net Balance</span>
            <span className={cn(
              "text-lg font-bold font-mono",
              totals.net_balance > 0 && "text-emerald-600",
              totals.net_balance < 0 && "text-red-600",
              totals.net_balance === 0 && "text-muted-foreground"
            )}>
              {totals.net_balance >= 0 ? '+' : ''}₹{formatAmount(totals.net_balance)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
